import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing untyped legacy interface; full typed migration tracked under COL-37 */
export interface TaskListModalData {
  title: string;         // e.g. 'In Progress' or 'Available'
  statusKey: string;     // e.g. 'in-progress' or 'templates'
  items: any[];
  mode?: 'tasks' | 'templates';
  searchPlaceholder?: string;
  actionLabel?: string;  // e.g. 'Start' (templates mode)
  showMandatory?: boolean;
  isRequiredTask?: (item: any) => boolean;
  showBulkActions?: boolean;
  getStatusBadgeClass?: (status: string) => string;
  getInitials?: (name: string) => string;
  getLocalDate?: (date: string) => string;
  onItemClick: (item: any) => void | Promise<string | null>;
  onEdit?: (item: any) => void;
  onViewReport?: (item: any) => void;
  onRowToggle?: (item: any, checked: boolean) => void;
  onDownloadPdf?: (merge: boolean) => Promise<void> | void;
  onDownloadMedia?: () => Promise<void> | void;
}
/* eslint-enable @typescript-eslint/no-explicit-any -- pre-existing untyped legacy interface; full typed migration tracked under COL-37 */

@Component({
  selector: 'app-task-list-modal',
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone migration tracked under COL-37
  standalone: false,
  templateUrl: './task-list-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './task-list-modal.component.scss',
})
export class TaskListModalComponent {
  searchTerm = '';
  isDownloadingPdf = false;
  isDownloadingMedia = false;
  mergePdfChecked = false;
  itemErrors: Record<number, string> = {};
  pendingItemId: number | null = null;

  onMergeCheck(event: Event): void {
    this.mergePdfChecked = (event.target as HTMLInputElement).checked;
  }

  /* eslint-disable @angular-eslint/prefer-inject -- constructor injection required for @Inject(MAT_DIALOG_DATA) token; migration tracked under COL-37 */
  constructor(
    public dialogRef: MatDialogRef<TaskListModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TaskListModalData,
  ) {}
  /* eslint-enable @angular-eslint/prefer-inject -- constructor injection required for @Inject(MAT_DIALOG_DATA) token; migration tracked under COL-37 */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped items array; typed migration tracked under COL-37
  get filteredItems(): any[] {
    const q = this.searchTerm.trim().toLowerCase();
    if (!q) return this.data.items;
    if (this.data.mode === 'templates') {
      return this.data.items.filter(item =>
        (item.name ?? '').toLowerCase().includes(q)
      );
    }
    return this.data.items.filter(item =>
      (item.title ?? '').toLowerCase().includes(q) ||
      (item.templateName ?? '').toLowerCase().includes(q) ||
      (item.createdByName ?? '').toLowerCase().includes(q)
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped item; typed migration tracked under COL-37
  async onItemClick(item: any): Promise<void> {
    if (this.pendingItemId !== null) return;
    this.pendingItemId = item.id;
    delete this.itemErrors[item.id];
    try {
      const result = await this.data.onItemClick(item);
      if (typeof result === 'string') {
        this.itemErrors[item.id] = result;
        return;
      }
      this.close();
    } finally {
      this.pendingItemId = null;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped item; typed migration tracked under COL-37
  onEdit(item: any): void {
    this.data.onEdit?.(item);
    this.close();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped item; typed migration tracked under COL-37
  onViewReport(item: any): void {
    this.data.onViewReport?.(item);
    this.close();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing untyped item; typed migration tracked under COL-37
  onCheckboxToggle(item: any, checked: boolean): void {
    item.selected = checked;
    this.data.onRowToggle?.(item, checked);
  }

  get hasSelected(): boolean {
    return this.data.items.some(i => !!i?.selected);
  }

  get selectedCount(): number {
    return this.data.items.reduce((acc, i) => acc + (i?.selected ? 1 : 0), 0);
  }

  get allSelected(): boolean {
    return this.data.items.length > 0 && this.data.items.every(i => !!i?.selected);
  }

  get someSelected(): boolean {
    return this.hasSelected && !this.allSelected;
  }

  toggleSelectAll(checked: boolean): void {
    this.data.items.forEach(item => {
      if (item.selected !== checked) {
        item.selected = checked;
        this.data.onRowToggle?.(item, checked);
      }
    });
  }

  async downloadPdf(): Promise<void> {
    if (this.isDownloadingPdf || !this.data.onDownloadPdf) return;
    this.isDownloadingPdf = true;
    try {
      await this.data.onDownloadPdf(this.mergePdfChecked);
    } finally {
      this.isDownloadingPdf = false;
    }
  }

  async downloadMedia(): Promise<void> {
    if (this.isDownloadingMedia || !this.data.onDownloadMedia) return;
    this.isDownloadingMedia = true;
    try {
      await this.data.onDownloadMedia();
    } finally {
      this.isDownloadingMedia = false;
    }
  }
}
